import { memo, useMemo } from "react";

import { useHandleApplyPrice } from "./hooks";

type Props = {
	// キャンセル料金
	cancelledPrice?: number;
	// 施設が上書きした金額
	hostInsertedPrice?: number;
	// ディスカウントされた金額
	discountedPrice?: number;
	// 通常料金
	price?: number;
};

/**
 * @example
 * ```
 * <ApplyPriceText price={1000} />
 * -> ¥ 1,000
 * ```
 *
 * ```
 * <ApplyPriceText price={1000} discountedPrice={800} />
 * -> ¥ 1,000（打ち消し）
 * -> ディスカウント適用後 ¥ 800
 * ```
 */
export const ApplyPriceText = memo(({ cancelledPrice, hostInsertedPrice, discountedPrice, price }: Props) => {
	const { type, applyPrice } = useHandleApplyPrice({ cancelledPrice, hostInsertedPrice, discountedPrice });

	const isDiscountedPrice = useMemo(() => type === "discounted-price", [type]);
	const isCancelledPrice = useMemo(() => type === "cancelled-price", [type]);
	const isHostInsertedPrice = useMemo(() => type === "host-inserted-price", [type]);

	/**
	 * 通常料金
	 */
	const priceText = useMemo(() => `¥ ${(price ?? 0).toLocaleString()}`, [price]);
	/**
	 * 適用金額
	 */
	const applyPriceText = useMemo(() => {
		return applyPrice === undefined ? `¥ 0` : `¥ ${applyPrice.toLocaleString()}`;
	}, [applyPrice]);

	/**
	 * キャンセル、もしくは施設が上書きがされた場合
	 */
	if (isCancelledPrice || isHostInsertedPrice) {
		return (
			<div className="text-right">
				<div className="flex justify-end gap-x-10">
					<p className="self-center text-xs underline">
						{isCancelledPrice ? "キャンセル料金" : "施設が上書きした金額"}
					</p>
					<p>{applyPriceText}</p>
				</div>
			</div>
		);
	}

	/**
	 * ディスカウントされていた場合
	 */
	if (isDiscountedPrice) {
		return (
			<div className="text-right">
				<p className="text-caption-50 line-through">{priceText}</p>

				<div className="flex justify-end gap-x-10">
					<p className="self-center text-xs underline">ディスカウント適用後</p>
					<p>{applyPriceText}</p>
				</div>
			</div>
		);
	}

	return (
		<div className="text-right">
			<p>{priceText}</p>
		</div>
	);
});
